/**
 * PersonaCellUrls — « URL les plus citées » (§4.5.3, last block of the persona view).
 *
 * Tier 2 of the loading contract: ONE read of the selected cell's `RUNX#` row
 * (`…/launches/:launchId/cells/:cellPath/sources`), on open, cached per cell (LRU 3).
 * The row is per CONFIGURATION — the block is therefore scoped to « configuration
 * sélectionnée · dernier lancement », exactly as the mock labels it; there is no
 * persona-wide URL aggregate anywhere in the data model (`ROLLUP.lastLaunch` folds
 * `topDomains` and `topTargets`, never `topUrls`).
 *
 * Residuals are never hidden: « + N autres URL » is part of the contract.
 */

import { useEffect, useRef, useState } from 'react';
import { Box, Chip, MenuItem, Select, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  cellPath as buildCellPath,
  type ConversationCell,
  type ConversationStage,
  type ConversationSubRunExtras,
} from '@traaker/shared';
import { api } from '../../services/api';
import { EngineLogo, ENGINE_META, engineKey } from '../brand/EngineLogo';
import DomainFavicon from '../competitors/DomainFavicon';
import { engineDisplay } from './personaFormat';

const CELL_SX = { borderBottom: '1px solid', borderColor: 'divider', px: 1, py: 0.75, fontSize: '0.8125rem', textAlign: 'left' as const };
const HEAD_SX = { ...CELL_SX, fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.06em', color: 'text.secondary', whiteSpace: 'nowrap' as const };

const STAGES: ConversationStage[] = ['exploration', 'evaluation', 'decision'];

function flagEmoji(cc: string): string {
  return [...cc.toUpperCase()].map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0))).join('');
}

export function PersonaCellUrls({
  brandId,
  personaId,
  launchId,
  cells,
}: {
  brandId: string;
  personaId: string;
  launchId: string;
  cells: ConversationCell[];
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>(() =>
    cells.length ? buildCellPath(cells[0].engine, cells[0].language, cells[0].zone) : '',
  );
  const [stageFilter, setStageFilter] = useState<ConversationStage | null>(null);
  const [extras, setExtras] = useState<ConversationSubRunExtras | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'empty'>('loading');
  const cache = useRef(new Map<string, ConversationSubRunExtras>());

  useEffect(() => {
    if (!selected) return;
    const key = `${launchId}|${selected}`;
    const hit = cache.current.get(key);
    if (hit) { setExtras(hit); setState('ready'); return; }
    let cancelled = false;
    setState('loading');
    api.get<ConversationSubRunExtras>(
      `/brands/${brandId}/personas/${personaId}/launches/${launchId}/cells/${selected}/sources`,
    ).then((r) => {
      if (cancelled) return;
      // LRU 3 (§4.3): three open cells is the working set; the fourth evicts the oldest.
      if (cache.current.size >= 3) cache.current.delete(cache.current.keys().next().value as string);
      cache.current.set(key, r);
      setExtras(r);
      setState('ready');
    }).catch(() => {
      if (!cancelled) { setExtras(null); setState('empty'); }
    });
    return () => { cancelled = true; };
  }, [brandId, personaId, launchId, selected]);

  const ownDomains = new Set((extras?.topDomains ?? []).filter((d) => d.ownDomain).map((d) => d.domain));
  const allUrls = extras?.topUrls ?? [];
  const urls = stageFilter ? allUrls.filter((u) => u.stage === stageFilter) : allUrls;
  const maxRuns = Math.max(...urls.map((u) => u.runs), 1);

  const stageLabel = (s: ConversationStage) =>
    t(`conversations.transcriptStage${s.charAt(0).toUpperCase()}${s.slice(1)}`);

  return (
    <Box>
      {/* Cell selector — one option per (zone × language × engine).
          "All zones" is not available: data is per-configuration, never aggregated across cells. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
          {t('conversations.persona.urlsCellLabel')}
        </Typography>
        <Select
          size="small"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          sx={{ fontSize: '0.8125rem' }}
        >
          {cells.map((c) => {
            const p = buildCellPath(c.engine, c.language, c.zone);
            const cc = c.zoneMeta?.countryCode;
            const flag = cc ? flagEmoji(cc) : '';
            const ek = engineKey(c.engine);
            const engineColor = ENGINE_META[ek]?.color ?? '#888';
            return (
              <MenuItem key={p} value={p} sx={{ fontSize: '0.8125rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {flag && <span style={{ fontSize: '1.1em', lineHeight: 1 }}>{flag}</span>}
                  <span>{c.zoneMeta?.label ?? c.zone}</span>
                  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', border: '1px solid', borderColor: 'divider', borderRadius: '3px', px: '3px', color: 'text.secondary', lineHeight: 1.6 }}>
                    {c.language}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: engineColor, borderRadius: '5px', px: 0.75, py: '1px' }}>
                    <EngineLogo engine={c.engine} size={14} />
                    <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: engineColor }}>{engineDisplay(c.engine)}</Box>
                  </Box>
                </Box>
              </MenuItem>
            );
          })}
        </Select>
        {cells.length === 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('conversations.persona.urlsAllZonesNote')}
          </Typography>
        )}
      </Box>

      {state === 'loading' && <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />}

      {state === 'empty' && (
        <Typography variant="body2" color="text.secondary">{t('conversations.persona.urlsEmpty')}</Typography>
      )}

      {state === 'ready' && allUrls.length === 0 && (
        <Typography variant="body2" color="text.secondary">{t('conversations.persona.urlsEmpty')}</Typography>
      )}

      {state === 'ready' && allUrls.length > 0 && (
        <>
          {/* Stage filter — filters the URL table client-side */}
          <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label={t('conversations.persona.filterAll')}
              size="small"
              variant={stageFilter === null ? 'filled' : 'outlined'}
              onClick={() => setStageFilter(null)}
              sx={{ cursor: 'pointer', fontSize: '0.6875rem' }}
            />
            {STAGES.map((s) => (
              <Chip
                key={s}
                label={stageLabel(s)}
                size="small"
                variant={stageFilter === s ? 'filled' : 'outlined'}
                onClick={() => setStageFilter(stageFilter === s ? null : s)}
                sx={{ cursor: 'pointer', fontSize: '0.6875rem' }}
              />
            ))}
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={HEAD_SX}>{t('conversations.persona.urlsColUrl')}</Box>
                  <Box component="th" sx={{ ...HEAD_SX, textAlign: 'right' as const }}>{t('conversations.persona.colCitations')}</Box>
                  <Box component="th" sx={{ ...HEAD_SX, textAlign: 'right' as const }}>{t('conversations.persona.colTurns')}</Box>
                  <Box component="th" sx={HEAD_SX}>{t('conversations.persona.colRuns')}</Box>
                  <Box component="th" sx={HEAD_SX}>{t('conversations.persona.urlsColStage')}</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {urls.map((u) => {
                  const own = ownDomains.has(u.domain);
                  return (
                    <Box component="tr" key={u.url} sx={own ? { bgcolor: 'action.hover' } : undefined}>
                      <Box component="td" sx={{ ...CELL_SX, fontFamily: 'monospace', fontSize: '0.75rem', overflowWrap: 'anywhere' as const, maxWidth: 400 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <DomainFavicon domain={u.domain} name={u.domain} size={16} />
                          <a href={u.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none', overflowWrap: 'anywhere', minWidth: 0 }}>
                            {u.url}
                          </a>
                        </Box>
                        {own && (
                          <Typography component="span" variant="caption" color="primary.main" fontWeight={700} sx={{ ml: 0.75 }}>
                            {t('conversations.runBrandsGroupOwn')}
                          </Typography>
                        )}
                      </Box>
                      <Box component="td" sx={{ ...CELL_SX, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' }}>{u.cit}</Box>
                      <Box component="td" sx={{ ...CELL_SX, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' }}>{u.turns}</Box>
                      <Box component="td" sx={CELL_SX}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ minWidth: 28, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                            {Math.round((u.runs / maxRuns) * 100)} %
                          </Typography>
                          <Box sx={{ width: 80, height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
                            <Box sx={{ width: `${(u.runs / maxRuns) * 100}%`, height: '100%', bgcolor: own ? 'primary.main' : 'text.disabled' }} />
                          </Box>
                        </Box>
                      </Box>
                      <Box component="td" sx={CELL_SX}>
                        {stageLabel(u.stage)}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
            {extras && extras.residualUrls.count > 0
              ? `${t('conversations.persona.urlsResidual', { count: extras.residualUrls.count, cit: extras.residualUrls.cit })} `
              : ''}
            {t('conversations.persona.urlsNote')}
          </Typography>
        </>
      )}
    </Box>
  );
}
